import { useQuery } from "@tanstack/react-query";
import * as salesService from "./services/SalesService";
import * as salesConfigurationService from "./services/SalesConfigurationService";
import { useBoundStore } from "store";

export const salesKeys = {
  all: ["sales"] as const,
  configurations: () => [...salesKeys.all, "configurations"] as const,
  info: (tenantId?: string, organizationId?: string) =>
    [...salesKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "info"] as const,
  targetVsActual: (tenantId?: string, organizationId?: string) =>
    [...salesKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "target-vs-actual"] as const,
  summaryCards: (tenantId?: string, organizationId?: string) =>
    [...salesKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "summary-cards"] as const,
  byUtility: (tenantId?: string, organizationId?: string) =>
    [...salesKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "by-utility"] as const,
  leaderboard: (tenantId?: string, organizationId?: string) =>
    [...salesKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "leaderboard"] as const,
  pipelineInfo: (tenantId?: string, organizationId?: string) =>
    [...salesKeys.all, "tenant", tenantId ?? "unknown", "organization", organizationId ?? "unknown", "pipeline", "info"] as const,
};

/**
 * Fetches all sales configurations.
 */
export function useSalesConfigurations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: salesKeys.configurations(),
    queryFn: () => salesConfigurationService.getSalesConfigurations(),
    staleTime: 5 * 60_000,
    ...options,
  });
}

/**
 * Consolidated dashboard hook — fetches all sales data in a single request.
 */
export function useSalesInfo(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: salesKeys.info(tenant?.id, organizationId),
    queryFn: () => salesService.getSalesInfo(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useTargetVsActualSales(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: salesKeys.targetVsActual(tenant?.id, organizationId),
    queryFn: () => salesService.getTargetVsActualSales(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useSalesSummaryCards(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: salesKeys.summaryCards(tenant?.id, organizationId),
    queryFn: () => salesService.getSalesSummaryCards(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useSalesByUtility(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: salesKeys.byUtility(tenant?.id, organizationId),
    queryFn: () => salesService.getSalesByUtility(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useSalesLeaderboard(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: salesKeys.leaderboard(tenant?.id, organizationId),
    queryFn: () => salesService.getSalesLeaderboard(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useSalesPipelineInfo(
  organizationId?: string,
  options?: { enabled?: boolean }
) {
  const { tenant } = useBoundStore();

  return useQuery({
    queryKey: salesKeys.pipelineInfo(tenant?.id, organizationId),
    queryFn: () => salesService.getSalesPipelineInfo(tenant!.id, organizationId as string),
    enabled: !!tenant?.id && !!organizationId && (options?.enabled ?? true),
    staleTime: 5 * 60_000,
    ...options,
  });
}
